import { useState } from 'react'
import { Button, EmptyState, SectionHeader } from '../../../shared/ui'
import CardEditDialog from './CardEditDialog'
import KartenStatusBadge from './KartenStatusBadge'
import PinDialog from './PinDialog'
import {
  datumDe, kartenFehler, kartenZustand, usePatchCard, useSchoolCards, type SchoolCard,
} from './kartenApi'
import { printSchoolCards } from './printSchoolCards'

interface Props {
  /** dim.School-Id als Text */
  schoolId: string
  schoolName: string
}

const zuletzt = (iso: string | null) => {
  if (!iso) return 'noch nie'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? datumDe(iso) : `${datumDe(iso)} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
}

export default function SchoolCardsPanel({ schoolId, schoolName }: Props) {
  const { data, isLoading, error } = useSchoolCards(schoolId)
  const patch = usePatchCard()
  const [dialog, setDialog] = useState<{ initial: SchoolCard | null } | null>(null)
  const [pinOpen, setPinOpen] = useState(false)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-on-surface-variant">
        <span className="material-symbols-outlined animate-spin text-3xl text-primary">progress_activity</span>
        <p className="text-xs font-medium">Karten werden geladen…</p>
      </div>
    )
  }
  if (error) return <p className="text-sm text-rose-600">{kartenFehler(error)}</p>

  const karten = data?.karten ?? []
  const pin = data?.pin ?? null
  const druckbar = karten.filter((k) => ['aktiv', 'bald'].includes(kartenZustand(k)))

  async function setStatus(k: SchoolCard, status: 'aktiv' | 'inaktiv') {
    try {
      setMsg(null)
      const r = await patch.mutateAsync({ id: k.id, status })
      setConfirmId(null)
      if (status === 'inaktiv') {
        setMsg(r.sitzungenBeendet > 0 ? `Karte gesperrt, ${r.sitzungenBeendet} Sitzung(en) beendet.` : 'Karte gesperrt.')
      }
    } catch (e) {
      setMsg(kartenFehler(e))
    }
  }

  return (
    <div className="space-y-6">
      <section>
        <SectionHeader label="PIN der Schule" />
        <div className="flex items-center justify-between gap-4 flex-wrap rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-3">
          <p className="text-sm text-on-surface">
            {pin
              ? <>PIN gesetzt am <span className="tabular-nums">{datumDe(pin.gesetztAm)}</span>{pin.gesetztVon ? <> von {pin.gesetztVon}</> : null}</>
              : 'Noch keine PIN'}
          </p>
          <Button variant="secondary" icon="password" onClick={() => setPinOpen(true)}>
            {pin ? 'Neue PIN erzeugen' : 'PIN erzeugen'}
          </Button>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SectionHeader label="Karten" />
          <div className="flex gap-2">
            <Button variant="secondary" icon="print" disabled={druckbar.length === 0} onClick={() => printSchoolCards(druckbar)}>
              Alle aktiven drucken
            </Button>
            <Button variant="primary" icon="add" onClick={() => setDialog({ initial: null })}>Karte hinzufügen</Button>
          </div>
        </div>

        {msg && <p className="text-sm text-on-surface-variant">{msg}</p>}

        {karten.length === 0 ? (
          <EmptyState icon="badge" title="Noch keine Karten" description="Mit „Karte hinzufügen“ die erste Karte dieser Schule anlegen." />
        ) : (
          <div className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest divide-y divide-outline-variant/20 overflow-hidden">
            {karten.map((k) => {
              const z = kartenZustand(k)
              const sperrt = confirmId === k.id
              return (
                <div key={k.id} className="px-4 py-3 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_auto_170px] gap-3 items-center">
                    <span className="font-mono text-sm font-semibold tabular-nums text-on-surface">{k.chip}</span>
                    <div className="min-w-0">
                      <p className="text-sm text-on-surface truncate">{k.bezeichnung}</p>
                      <p className="text-[11px] text-on-surface-variant tabular-nums">
                        {datumDe(k.gueltigVon)} – {k.gueltigBis ? datumDe(k.gueltigBis) : 'unbefristet'} · zuletzt benutzt: {zuletzt(k.letzteAnmeldung)}
                      </p>
                    </div>
                    <KartenStatusBadge zustand={z} />
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" icon="edit" onClick={() => setDialog({ initial: k })}>Bearbeiten</Button>
                      <Button variant="ghost" size="sm" icon="print" onClick={() => printSchoolCards([k])}>Drucken</Button>
                      {k.status === 'aktiv'
                        ? <Button variant="ghost" size="sm" icon="lock" onClick={() => setConfirmId(k.id)}>Sperren</Button>
                        : <Button variant="ghost" size="sm" icon="lock_open" loading={patch.isPending} onClick={() => setStatus(k, 'aktiv')}>Entsperren</Button>}
                    </div>
                  </div>
                  {sperrt && (
                    <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-sm text-rose-700 dark:text-rose-300">
                        Karte sperren? Wer gerade mit dieser Karte angemeldet ist, wird abgemeldet.
                      </p>
                      <div className="flex gap-2">
                        <Button variant="secondary" size="sm" onClick={() => setConfirmId(null)} disabled={patch.isPending}>Abbrechen</Button>
                        <Button variant="danger" size="sm" loading={patch.isPending} onClick={() => setStatus(k, 'inaktiv')}>Sperren</Button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      <CardEditDialog
        open={!!dialog}
        schoolId={schoolId}
        initial={dialog?.initial ?? null}
        onClose={() => setDialog(null)}
      />
      <PinDialog open={pinOpen} schoolId={schoolId} schoolName={schoolName} onClose={() => setPinOpen(false)} />
    </div>
  )
}
